import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listKafDomains, type KafDomain } from "@/lib/kaf-academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { SpiralJourney, type SpiralPoint } from "@/academy/engines/living/SpiralJourney";
import { ArrowRight, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { ACADEMY_ID, breadcrumbs, courseNode, ldScript, seoMeta } from "@/lib/schema";
import { absoluteUrl } from "@/lib/site";

const KAF_TITLE = "KAfrequency Academy — A Living Operating System | Buddha KA";
const KAF_DESCRIPTION =
  "Enter the KAfrequency Academy: attention, perception, emotional balance, creativity, wisdom, communication, discipline, and community — a living, interactive learning world.";

export const Route = createFileRoute("/academy/kafrequency/")({
  loader: () => listKafDomains(),
  head: ({ loaderData }) => ({
    ...seoMeta({ title: KAF_TITLE, description: KAF_DESCRIPTION, path: "/academy/kafrequency" }),
    scripts: [
      ldScript(
        courseNode({
          id: `${absoluteUrl("/academy/kafrequency")}#course`,
          url: absoluteUrl("/academy/kafrequency"),
          name: "KAfrequency Academy",
          description: KAF_DESCRIPTION,
          partOfCourseId: ACADEMY_ID,
          lessons: (loaderData ?? []).map((d) => ({
            path: `/academy/kafrequency/${d.slug}`,
            name: d.name,
          })),
        }),
      ),
      ldScript(breadcrumbs([["Academy", "/academy"], ["KAfrequency", "/academy/kafrequency"]])),
    ],
  }),
  component: KafHub,
});


function KafHub() {
  const initial = Route.useLoaderData();
  const { data: domains = initial } = useQuery({
    queryKey: ["kaf", "domains"],
    queryFn: () => listKafDomains(),
    initialData: initial,
  });
  const [selected, setSelected] = useState<SpiralPoint | null>(null);

  const points = useMemo<SpiralPoint[]>(
    () => ((domains ?? []) as KafDomain[]).map((d) => ({ id: d.id, slug: d.slug, label: d.name, hue: d.hue, order: d.spiral_order })),
    [domains],
  );
  const selectedDomain = selected ? ((domains ?? []) as KafDomain[]).find((d) => d.id === selected.id) : null;

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><LivingBackdrop hue={44} density={110} /></div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 pt-16 pb-8 sm:px-6 lg:pt-24">
          <p className="text-xs uppercase tracking-[0.35em] text-gold">The KAfrequency Academy</p>
          <h1 className="mt-3 font-serif text-4xl leading-tight sm:text-6xl">
            A living operating system<br/>for human capacity.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-foreground/80">
            Not a course library. A world you enter. Train attention, perception,
            emotion, creativity, wisdom, communication, discipline, and community
            through living lessons, daily practice, and your own reflection.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-4 pb-16 sm:px-6 lg:grid-cols-[1fr,320px]">
        <div>
          <SpiralJourney points={points} onSelect={(p) => setSelected(p)} selectedId={selected?.id ?? null} />
          <p className="mt-3 text-center text-xs text-foreground/50">Tap a node to enter that domain.</p>
        </div>
        <aside className="rounded-2xl border hairline bg-obsidian/70 p-5">
          {selectedDomain ? (
            <>
              <div className="text-xs uppercase tracking-[0.3em]" style={{ color: `hsl(${selectedDomain.hue} 85% 65%)` }}>
                Domain
              </div>
              <h2 className="mt-2 font-serif text-2xl text-gold-luminous">{selectedDomain.name}</h2>
              {selectedDomain.tagline && <p className="mt-1 text-sm uppercase tracking-widest text-foreground/60">{selectedDomain.tagline}</p>}
              {selectedDomain.description && <p className="mt-3 text-sm text-foreground/80">{selectedDomain.description}</p>}
              <Link
                to="/academy/kafrequency/$domain"
                params={{ domain: selectedDomain.slug }}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian"
              >
                Enter {selectedDomain.name} <ArrowRight size={14} />
              </Link>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
                <Sparkles size={14} /> The Spiral
              </div>
              <p className="mt-3 text-sm text-foreground/80">
                Every domain is a living harmonic. Move around the spiral —
                each node opens a world of practice.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-2">
                {((domains ?? []) as KafDomain[]).map((d) => (
                  <Link key={d.id} to="/academy/kafrequency/$domain" params={{ domain: d.slug }}
                    className="rounded-lg border hairline px-3 py-2 text-xs uppercase tracking-widest text-foreground/70 hover:border-gold/60 hover:text-foreground">
                    {d.name}
                  </Link>
                ))}
              </div>
            </>
          )}
        </aside>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-24 sm:px-6">
        <h2 className="font-serif text-2xl text-gold-luminous">Living rhythm</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <PortalCard to="/academy/kaf/daily" title="Daily Practice" body="A fresh practice each day. Breath. Reflection. Making." />
          <PortalCard to="/academy/kaf/journal" title="Private Journal" body="Encrypted-at-rest reflections. Track mood, tags, arcs." />
          <PortalCard to="/academy/kaf/studio" title="Creative Studio" body="Drafts for writing, music, visual, and projects." />
        </div>
      </section>
    </div>
  );
}

function PortalCard({ to, title, body }: { to: string; title: string; body: string }) {
  return (
    <Link to={to as never} className="group rounded-2xl border hairline bg-obsidian/50 p-5 transition hover:border-gold/60">
      <h3 className="font-serif text-lg text-gold-luminous">{title}</h3>
      <p className="mt-2 text-sm text-foreground/75">{body}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs text-gold group-hover:gap-2">Enter <ArrowRight size={12} /></span>
    </Link>
  );
}
